import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery, useQueryClient} from 'react-query';
import {useAxios} from 'src/utils/http';
import {Project, fetchProject} from './project';

const PROJECT_ID_STORAGE_KEY = 'project_id';
const PROJECT_ID_SESSION_KEY = 'project_id';

interface ProjectContextType {
  projectId: string | null;
  project: Project | undefined;
  setProjectId: (id: string | null) => void;
  isLoading: boolean;
}

const ProjectContext = createContext<ProjectContextType | undefined>(undefined);

export const useProjectContext = () => {
  const context = useContext(ProjectContext);
  if (context === undefined) {
    throw new Error(
      'useProjectContext must be used within a ProjectContextProvider'
    );
  }
  return context;
};

interface ProjectContextProviderProps {
  children: ReactNode;
  requireProjectId?: boolean;
}

export const ProjectContextProvider = ({
  children,
  requireProjectId = true,
}: ProjectContextProviderProps) => {
  const navigate = useNavigate();
  const http = useAxios();
  const queryClient = useQueryClient();
  const [projectId, setProjectIdState] = useState<string | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize project_id from storage on mount
  useEffect(() => {
    // First, check sessionStorage (current window's context)
    let storedProjectId = sessionStorage.getItem(PROJECT_ID_SESSION_KEY);

    // If not in sessionStorage, check localStorage and copy it
    if (!storedProjectId) {
      storedProjectId = localStorage.getItem(PROJECT_ID_STORAGE_KEY);
      if (storedProjectId) {
        // Copy from localStorage to sessionStorage
        sessionStorage.setItem(PROJECT_ID_SESSION_KEY, storedProjectId);
      }
    }

    setProjectIdState(storedProjectId);
    setIsInitialized(true);
  }, []);

  // Note: no 'storage' event listener here. Each tab keeps its own project
  // context in sessionStorage; localStorage is only a "last used" hint for
  // new tabs on first mount (see init effect above). Cross-tab propagation
  // via StorageEvent was previously broken — it fired for localStorage, but
  // re-read sessionStorage (still stale in the other tab) and no-op'd, while
  // the HTTP interceptor reads sessionStorage — ending up with the UI on one
  // project and the x-project-id header on another. Keeping tabs isolated is
  // the simpler, correct model; the server-side middleware validates that the
  // header matches the :project_id path variable (routes.go) as a backstop.

  // Fetch project from API when projectId is available
  const {
    data: project,
    isLoading,
    isFetching,
  } = useQuery(
    ['project', projectId],
    () => {
      if (!projectId) {
        return Promise.resolve(undefined);
      }
      return fetchProject(http, projectId);
    },
    {
      enabled: !!projectId && isInitialized,
      staleTime: 1 * 60 * 1000, // 1 minute
      cacheTime: 5 * 60 * 1000, // 5 minutes
    }
  );

  // Update cache when project changes
  useEffect(() => {
    if (projectId && project) {
      // Set the project data in cache for this projectId
      queryClient.setQueryData(['project', projectId], project);
    } else if (projectId === null) {
      // Clear project cache when projectId is cleared
      queryClient.removeQueries(['project', null]);
    }
  }, [projectId, project, queryClient]);

  // Redirect to /projects if project_id is required but not set
  useEffect(() => {
    if (isInitialized && requireProjectId && !projectId) {
      navigate('/project');
    }
  }, [isInitialized, requireProjectId, projectId, navigate]);

  const setProjectId = useCallback(
    (id: string | null) => {
      setProjectIdState(id);
      if (id) {
        // Save to both localStorage (persistent) and sessionStorage (current window)
        localStorage.setItem(PROJECT_ID_STORAGE_KEY, id);
        sessionStorage.setItem(PROJECT_ID_SESSION_KEY, id);
        // Invalidate previous project cache and fetch new one
        queryClient.invalidateQueries(['project']);
      } else {
        // Clear both storages
        localStorage.removeItem(PROJECT_ID_STORAGE_KEY);
        sessionStorage.removeItem(PROJECT_ID_SESSION_KEY);
        // Remove project from cache
        queryClient.removeQueries(['project']);
      }
    },
    [queryClient]
  );

  // Don't render children until initialized to avoid flash of redirect
  if (!isInitialized) {
    return null;
  }

  // Don't render children if project_id is required but not set (will redirect)
  if (requireProjectId && !projectId) {
    return null;
  }

  // Ensure project is undefined when projectId is null
  const projectData = projectId ? project : undefined;

  return (
    <ProjectContext.Provider
      value={{
        projectId,
        project: projectData,
        setProjectId,
        isLoading: isLoading || isFetching,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
